import { Card, Col, message, Row, Tag, Typography, Statistic, Progress, Button, Space } from "antd";
import { useContext, useEffect, useState } from "react";
import SearchInput from "../../components/SearchInput";
import { useNavigate, useParams } from "react-router";
import {
	PiCloudDuotone,
	PiHardDrivesDuotone,
	PiGlobeDuotone,
	PiCubeDuotone,
	PiMoneyDuotone,
	PiGearDuotone,
	PiPlusDuotone,
	PiMonitorDuotone,
	PiReceiptXDuotone,
} from "react-icons/pi";
import AppContext from "../../store/app.context";
import { formatCurrency } from "../../utils/Formatter";
import {
	requestGetServiceGroupList,
	requestGetServiceList,
	requestGetStatistics,
} from "../../services/user.service";

const serviceIcons = {
	"APLA.DB": <PiHardDrivesDuotone size={32} color="#52c41a" />,
	"APLA.AP": <PiCloudDuotone size={32} color="#722ed1" />,
	"APLA.SV": <PiGearDuotone size={32} color="#FA1680FF" />,
	WEHO: <PiMonitorDuotone size={32} color="#fa8c16" />,
};

function DashboardPage() {
	const context = useContext(AppContext);
	const params = useParams();
	const navigate = useNavigate();
	const user = context.user;
	const teams = user.teams;
	// get teamId from param
	const teamId = params.teamId;

	const [services, setServices] = useState([]);
	const [serviceGroups, setServiceGroups] = useState([]);
	const [statsLoading, setStatsLoading] = useState(false);

	const [dashboardStats, setDashboardStats] = useState({
		totalSpent: 0,
		instances: 0,
		domains: 0,
		unpaidInvoices: 0,
	});

	const [mappedServices, setMappedServices] = useState([]);
	const [selectedServiceGroup, setSelectedServiceGroup] = useState("all");

	useEffect(() => {
		if (!teamId || !teams?.find((team) => team._id === teamId)) {
			navigate(`/${user.defaultTeamId}/dashboard`);
		}
	}, [context.isLoggedIn, user, teamId, teams]);

	// set document title
	useEffect(() => {
		document.title = "Cloudiva App | Dashboard";
	}, []);

	useEffect(() => {
		if (context.isLoggedIn && teamId && teams && teams.length > 0) {
			fetchServiceGroups();
			fetchDashboardStats();
		}
	}, [context.isLoggedIn, teamId, teams]);

	useEffect(() => {
		if (services.length > 0) {
			handleGroupFilter("all");
		}
	}, [services]);

	const fetchDashboardStats = () => {
		setStatsLoading(true);

		requestGetStatistics(teamId)
			.then((response) => {
				const stats = response.data.data;
				setDashboardStats({
					...stats,
				});
			})
			.catch((error) => {
				console.error("Error fetching dashboard stats:", error);
				message.error("Failed to load dashboard statistics");
			})
			.finally(() => {
				setStatsLoading(false);
			});
	};

	const fetchServices = () => {
		requestGetServiceList({
			page: 1,
			limit: 10,
			order: "_id:desc",
		})
			.then((response) => {
				const tempServices = response.data.data;

				setServices(tempServices);
			})
			.catch((error) => {});
	};

	const fetchServiceGroups = () => {
		requestGetServiceGroupList({
			page: 1,
			limit: 100,
			order: "_id:desc",
		})
			.then((response) => {
				const tempServiceGroups = response.data.data;

				setServiceGroups(tempServiceGroups);
				fetchServices();
			})
			.catch((error) => {});
	};

	function checkMatch(input, searchTerm) {
		const regex = new RegExp(searchTerm.split("").join(".*?"), "gi");
		return regex.test(input);
	}

	const handleSearch = (value) => {
		let filteredServices = services;

		// Filter by group
		if (selectedServiceGroup !== "all") {
			filteredServices = filteredServices.filter(
				(service) => service.groupId === selectedServiceGroup
			);
		}

		// Filter by search term
		if (value) {
			filteredServices = filteredServices.filter((service) => {
				return (
					service.name
						.toLowerCase()
						.replace(/[^a-zA-Z0-9]/g, "")
						.includes(value.toLowerCase().replace(/[^a-zA-Z0-9]/g, "")) ||
					checkMatch(service.name, value)
				);
			});
		}

		setMappedServices(filteredServices);
	};

	const handleGroupFilter = (groupId) => {
		setSelectedServiceGroup(groupId);
		let filteredServices = services;

		if (groupId !== "all") {
			filteredServices = services.filter((service) => service.groupId === groupId);
		}

		setMappedServices(filteredServices);
	};

	const getStatusColor = (status) => {
		switch (status) {
			case "success":
				return "#52c41a";
			case "warning":
				return "#faad14";
			case "error":
				return "#f5222d";
			default:
				return "#1890ff";
		}
	};

	return (
		<div>
			{/* Dashboard Statistics */}
			<Row gutter={[24, 24]} style={{ marginBottom: 24 }}>
				<Col xs={24} sm={12} md={6}>
					<Card loading={statsLoading}>
						<Statistic
							title="Total Pengeluaran"
							value={formatCurrency(dashboardStats.totalSpent)}
							precision={2}
							prefix={<PiMoneyDuotone />}
							valueStyle={{ color: "#3f8600" }}
						/>
					</Card>
				</Col>
				<Col xs={24} sm={12} md={6}>
					<Card loading={statsLoading}>
						<Statistic
							title="Layanan Anda"
							value={dashboardStats.instances}
							prefix={<PiCubeDuotone />}
							valueStyle={{ color: "#1890ff" }}
						/>
					</Card>
				</Col>
				<Col xs={24} sm={12} md={6}>
					<Card loading={statsLoading}>
						<Statistic
							title="Domain Anda"
							value={dashboardStats.domains}
							prefix={<PiGlobeDuotone />}
							valueStyle={{ color: "#722ed1" }}
						/>
					</Card>
				</Col>
				<Col xs={24} sm={12} md={6}>
					<Card loading={statsLoading}>
						<Statistic
							title="Invoice Belum Dibayar"
							prefix={<PiReceiptXDuotone />}
							value={dashboardStats.unpaidInvoices}
							valueStyle={{ color: "#52c41a" }}
						/>
					</Card>
				</Col>
			</Row>

			{/* Quick Actions & Recent Activity */}
			<Row gutter={[24, 24]} style={{ marginBottom: 24 }}>
				<Col xs={24} lg={24}>
					<Card title="Aksi Cepat" size="small">
						<Row gutter={[8, 8]}>
							<Col>
								<Button icon={<PiPlusDuotone />}>Tambah Layanan</Button>
							</Col>
							<Col>
								<Button icon={<PiPlusDuotone />}>Daftarkan Domain</Button>
							</Col>
						</Row>
					</Card>
				</Col>
			</Row>

			{/* Recent Activities */}
			{/* <Row gutter={[24, 24]} style={{ marginBottom: 24 }}>
				<Col span={24}>
					<Card title="Recent Activities" size="small">
						<div style={{ maxHeight: 200, overflowY: "auto" }}>
							{recentActivities.map((activity) => (
								<div
									key={activity.id}
									style={{
										display: "flex",
										alignItems: "center",
										padding: "8px 0",
										borderBottom: "1px solid #f0f0f0",
									}}>
									<div style={{ marginRight: 12 }}>{activity.icon}</div>
									<div style={{ flex: 1 }}>
										<Typography.Text>{activity.message}</Typography.Text>
										<div>
											<Typography.Text type="secondary" style={{ fontSize: 12 }}>
												{activity.time}
											</Typography.Text>
										</div>
									</div>
								</div>
							))}
						</div>
					</Card>
				</Col>
			</Row> */}

			{/* Services Grid */}
			<Row gutter={[24, 24]}>
				<Col span={24}>
					<Card
						title="Layanan Tersedia"
						size="default"
						extra={
							<Row gutter={[8, 8]}>
								<Col>
									<SearchInput
										placeholder="Cari layanan..."
										onChange={handleSearch}
										style={{ width: 250 }}
									/>
								</Col>
								<Col>
									<Button
										type="primary"
										icon={<PiPlusDuotone />}
										onClick={() => navigate(`/${teamId}/instances/add`)}>
										Install Layanan
									</Button>
								</Col>
							</Row>
						}>
						<Space direction="vertical" style={{ width: "100%" }} size="middle">
							<Space>
								<Button
									key={"all"}
									size="small"
									type={selectedServiceGroup === "all" ? "primary" : "default"}
									onClick={() => handleGroupFilter("all")}>
									All ({services.length})
								</Button>
								{serviceGroups.map((group) => (
									<Button
										key={group._id}
										size="small"
										type={selectedServiceGroup === group._id ? "primary" : "default"}
										onClick={() => handleGroupFilter(group._id)}>
										{group.name} ({services.filter((s) => s.groupId === group._id).length})
									</Button>
								))}
							</Space>
							<Row gutter={[24, 24]}>
								{mappedServices.map((service) => (
									<Col key={service._id} xs={24} sm={12} md={8} lg={6}>
										<Card
											style={{
												cursor: "pointer",
												height: "100%",
											}}
											onClick={() => {
												navigate(`/services/${service._id}/edit`);
											}}
											size="small"
											hoverable>
											<div
												style={{
													display: "flex",
													flexDirection: "column",
													gap: 12,
													height: "100%",
												}}>
												<div
													style={{
														display: "flex",
														justifyContent: "space-between",
														alignItems: "center",
													}}>
													{serviceIcons[service.code] || (
														<PiGearDuotone size={32} color="#fa8c16" />
													)}
													<Tag color={service.status === "ACTIVE" ? "success" : "error"}>
														{service.status === "ACTIVE" ? "Aktif" : "Tidak Aktif"}
													</Tag>
												</div>

												<div>
													<Typography.Text strong>{service.name}</Typography.Text>
													<div style={{ marginTop: 4 }}>
														<Typography.Text type="secondary" style={{ fontSize: 12 }}>
															{service.code}
														</Typography.Text>
													</div>
												</div>

												<div style={{ marginTop: "auto" }}>
													<div
														style={{
															display: "flex",
															justifyContent: "space-between",
															alignItems: "center",
															marginBottom: 8,
														}}>
														<Typography.Text strong style={{ color: "#1890ff" }}>
															{service.price}
														</Typography.Text>
														<Typography.Text type="secondary" style={{ fontSize: 12 }}>
															{0}/{0} used
														</Typography.Text>
													</div>
													<Progress
														percent={(0 / 0) * 100}
														size="small"
														strokeColor={{
															"0%": "#108ee9",
															"100%": "#87d068",
														}}
														showInfo={false}
													/>
												</div>
											</div>
										</Card>
									</Col>
								))}
							</Row>
							{mappedServices.length === 0 && (
								<div style={{ textAlign: "center", padding: "40px 0" }}>
									<Typography.Text type="secondary">
										No services found matching your criteria
									</Typography.Text>
								</div>
							)}
						</Space>
					</Card>
				</Col>
			</Row>
		</div>
	);
}

export default DashboardPage;
